import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@wastelinq/auth';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  LocalShipping as LocalShippingIcon,
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  TrendingUp,
  TrendingDown,
  Warning,
} from '@mui/icons-material';
import ErrorBoundary from './components/ErrorBoundary';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: { retry: 1 },
  },
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

const drawerWidth = 240;

function useRouter() {
  const [route, setRoute] = React.useState('dashboard');
  return { route, navigate: setRoute };
}

function DashboardView() {
  const stats = [
    { label: 'Total Orders', value: '2,847', change: '+12.5%', trend: 'up' },
    { label: 'Active Profiles', value: '156', change: '+8.2%', trend: 'up' },
    { label: 'Containers', value: '1,429', change: '-2.4%', trend: 'down' },
    { label: 'Revenue', value: '$94.2k', change: '+18.7%', trend: 'up' },
  ];

  const recentOrders = [
    { id: '#2847', generator: 'ABC Corporation', profile: 'Hazardous Type A', status: 'In Progress', date: 'Oct 5, 2025' },
    { id: '#2846', generator: 'XYZ Industries', profile: 'Non-Hazardous B', status: 'Completed', date: 'Oct 4, 2025' },
    { id: '#2845', generator: 'Tech Solutions', profile: 'Universal Waste', status: 'Pending', date: 'Oct 3, 2025' },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Dashboard Overview
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {stats.map((stat, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  {stat.label}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                  {stat.value}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {stat.trend === 'up' ? (
                    <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                  ) : (
                    <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{ color: stat.trend === 'up' ? 'success.main' : 'error.main' }}
                  >
                    {stat.change}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    vs last month
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Recent Orders
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Generator</TableCell>
                  <TableCell>Profile</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>{order.id}</TableCell>
                    <TableCell>{order.generator}</TableCell>
                    <TableCell>{order.profile}</TableCell>
                    <TableCell>
                      <Chip
                        label={order.status}
                        size="small"
                        color={
                          order.status === 'Completed' ? 'success' :
                          order.status === 'In Progress' ? 'primary' : 'warning'
                        }
                      />
                    </TableCell>
                    <TableCell>{order.date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

function OrdersView() {
  const orders = [
    { id: '#2847', generator: 'ABC Corporation', profile: 'Hazardous Type A', containers: 3, status: 'In Progress', pickup: 'Oct 8, 2025', amount: '$2,400' },
    { id: '#2846', generator: 'XYZ Industries', profile: 'Non-Hazardous B', containers: 5, status: 'Completed', pickup: 'Oct 6, 2025', amount: '$1,800' },
    { id: '#2845', generator: 'Tech Solutions', profile: 'Universal Waste', containers: 2, status: 'Pending', pickup: 'Oct 10, 2025', amount: '$3,200' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Orders Management
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />}>
          New Order
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search orders..."
              size="small"
              sx={{ flexGrow: 1, minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="outlined" startIcon={<FilterListIcon />}>
              Filters
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Generator</TableCell>
                <TableCell>Profile</TableCell>
                <TableCell align="center">Containers</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Pickup Date</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{order.id}</TableCell>
                  <TableCell>{order.generator}</TableCell>
                  <TableCell>{order.profile}</TableCell>
                  <TableCell align="center">{order.containers}</TableCell>
                  <TableCell>
                    <Chip
                      label={order.status}
                      size="small"
                      color={
                        order.status === 'Completed' ? 'success' :
                        order.status === 'In Progress' ? 'primary' : 'warning'
                      }
                    />
                  </TableCell>
                  <TableCell>{order.pickup}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{order.amount}</TableCell>
                  <TableCell>
                    <Button size="small">View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}

function ProfilesView() {
  const profiles = [
    { id: 'WP-567', name: 'Hazardous Waste Type A', status: 'Approved', company: 'ABC Corp', epaCode: 'D001' },
    { id: 'WP-568', name: 'Non-Hazardous Type B', status: 'Under Review', company: 'XYZ Industries', epaCode: 'Non-RCRA' },
    { id: 'WP-569', name: 'Universal Waste', status: 'Pending', company: 'Tech Solutions', epaCode: 'Universal' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Waste Profiles
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />}>
          New Profile
        </Button>
      </Box>

      <Grid container spacing={3}>
        {profiles.map((profile) => (
          <Grid item xs={12} md={6} lg={4} key={profile.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Chip
                    label={profile.status}
                    size="small"
                    color={
                      profile.status === 'Approved' ? 'success' :
                      profile.status === 'Under Review' ? 'info' : 'warning'
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    {profile.id}
                  </Typography>
                </Box>
                <Typography variant="h6" gutterBottom>
                  {profile.name}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Company:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {profile.company}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      EPA Code:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                      {profile.epaCode}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function InventoryView() {
  const inventory = [
    { barcode: 'WL-001234', type: '55-Gallon Drum', status: 'Available', location: 'Warehouse A' },
    { barcode: 'WL-001235', type: '30-Gallon Drum', status: 'In Use', location: 'Generator Site - ABC' },
    { barcode: 'WL-001236', type: '85-Gallon Overpack', status: 'In Transit', location: 'En Route' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Container Inventory
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />}>
          Scan Container
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: '1,429' },
          { label: 'Available', value: '856' },
          { label: 'In Use', value: '423' },
          { label: 'In Transit', value: '150' },
        ].map((stat, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {stat.label}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Barcode</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventory.map((item) => (
                <TableRow key={item.barcode} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'primary.main' }}>
                    {item.barcode}
                  </TableCell>
                  <TableCell>{item.type}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.status}
                      size="small"
                      color={
                        item.status === 'Available' ? 'success' :
                        item.status === 'In Use' ? 'info' : 'warning'
                      }
                    />
                  </TableCell>
                  <TableCell>{item.location}</TableCell>
                  <TableCell>
                    <Button size="small">Track</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}

function ReportsView() {
  const reports = [
    { name: 'Monthly Waste Summary', date: 'Sep 30, 2025', type: 'PDF', size: '2.4 MB' },
    { name: 'Compliance Report Q3', date: 'Sep 30, 2025', type: 'Excel', size: '1.8 MB' },
    { name: 'Container Utilization', date: 'Oct 1, 2025', type: 'PDF', size: '3.1 MB' },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Reports & Analytics
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Report Type" size="small" select SelectProps={{ native: true }}>
                <option value="">All Reports</option>
                <option value="waste">Waste Summary</option>
                <option value="compliance">Compliance</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Date From" type="date" size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Date To" type="date" size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
          <Button variant="contained" sx={{ mt: 2 }} startIcon={<FilterListIcon />}>
            Generate Custom Report
          </Button>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {reports.map((report, i) => (
          <Grid item xs={12} md={6} lg={4} key={i}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Chip label={report.type} size="small" />
                </Box>
                <Typography variant="h6" gutterBottom>
                  {report.name}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {report.size}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {report.date}
                  </Typography>
                </Box>
                <Button variant="outlined" fullWidth size="small">
                  Download
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function AppContent() {
  const { route, navigate } = useRouter();

  const menuItems = [
    { name: 'Dashboard', route: 'dashboard', icon: <DashboardIcon /> },
    { name: 'Orders', route: 'orders', icon: <LocalShippingIcon /> },
    { name: 'Profiles', route: 'profiles', icon: <DescriptionIcon /> },
    { name: 'Inventory', route: 'inventory', icon: <InventoryIcon /> },
    { name: 'Reports', route: 'reports', icon: <AssessmentIcon /> },
  ];

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
            WasteLinQ Reimagined
          </Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.route} disablePadding>
                <ListItemButton
                  selected={route === item.route}
                  onClick={() => navigate(item.route)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
        <Box sx={{ p: 2, mt: 'auto', borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>A</Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Admin User
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Administrator
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: 'background.default' }}>
        <Toolbar />
        {route === 'dashboard' && <DashboardView />}
        {route === 'orders' && <OrdersView />}
        {route === 'profiles' && <ProfilesView />}
        {route === 'inventory' && <InventoryView />}
        {route === 'reports' && <ReportsView />}
      </Box>
    </Box>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <AuthProvider apiBaseUrl="https://reimagine-api.ai-smith.net" storageKey="wastelinq_auth">
            <AppContent />
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
